/**
 * TV Display Page
 *
 * Full-screen dashboard optimized for TV/monitor display
 * No navigation - just data visualization with auto-refresh
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { tvDisplayAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'

// Format currency (cents to dollars)
const formatCurrency = (cents) => {
  if (cents === null || cents === undefined) return '$0'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(cents / 100)
}

// Format large numbers
const formatNumber = (num) => {
  if (num === null || num === undefined) return '0'
  return new Intl.NumberFormat('en-US').format(num)
}

// Format percentage
const formatPercent = (value) => {
  if (value === null || value === undefined) return '0%'
  return `${value.toFixed(1)}%`
}

// Summary Card Component (larger for TV)
function SummaryCard({ title, value, subtitle, trend, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-900/50 border-blue-500/30',
    green: 'bg-green-900/50 border-green-500/30',
    yellow: 'bg-yellow-900/50 border-yellow-500/30',
    red: 'bg-red-900/50 border-red-500/30'
  }

  return (
    <div className={`rounded-2xl border-2 p-6 ${colorClasses[color]}`}>
      <div className="text-gray-400 text-lg font-medium uppercase tracking-wider mb-2">
        {title}
      </div>
      <div className="text-5xl font-bold text-white mb-2">
        {value}
      </div>
      {subtitle && (
        <div className="text-gray-400 text-lg">
          {subtitle}
        </div>
      )}
    </div>
  )
}

export default function TVDisplayPage() {
  const navigate = useNavigate()
  const { user, isAuthenticated, logout } = useAuthStore()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [currentTime, setCurrentTime] = useState(new Date())

  // Redirect non-tv_display users
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    if (user?.role !== 'tv_display') {
      navigate('/')
      return
    }
  }, [isAuthenticated, user, navigate])

  // Update clock every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  // Fetch dashboard data
  const fetchData = useCallback(async () => {
    try {
      setError(null)
      const response = await tvDisplayAPI.getDashboard()

      if (response.data.success) {
        setData(response.data.data)
        setLastUpdated(new Date())
      } else {
        setError(response.data.error?.message || 'Failed to load data')
      }
    } catch (err) {
      console.error('[TV DISPLAY] Error fetching data:', err)
      setError(err.response?.data?.error?.message || 'Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }, [])

  // Initial load and auto-refresh
  useEffect(() => {
    fetchData()

    // Get refresh interval from user settings (default 15 minutes)
    const refreshInterval = (user?.tvDisplaySettings?.refreshInterval || 15) * 60 * 1000

    const interval = setInterval(() => {
      console.log('[TV DISPLAY] Auto-refreshing...')
      fetchData()
    }, refreshInterval)

    return () => clearInterval(interval)
  }, [fetchData, user?.tvDisplaySettings?.refreshInterval])

  // Prepare chart data
  const pieData = data?.brand_breakdown?.map(brand => ({
    name: brand.displayName,
    value: brand.summary.total_revenue / 100,
    color: brand.color
  })) || []

  const areaData = data?.daily?.map(day => {
    const entry = { date: day.displayDate }
    Object.entries(day.by_brand || {}).forEach(([brandId, brandData]) => {
      entry[brandId] = brandData.revenue / 100
    })
    entry.total = day.total_revenue / 100
    return entry
  }) || []

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <div className="text-2xl text-gray-400">Loading Dashboard...</div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center max-w-lg">
          <div className="text-6xl mb-4">&#9888;</div>
          <div className="text-2xl text-red-400 mb-4">{error}</div>
          <div className="flex gap-4 justify-center">
            <button
              onClick={fetchData}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg text-xl hover:bg-blue-700"
            >
              Retry
            </button>
            <button
              onClick={() => {
                logout()
                navigate('/login')
              }}
              className="px-6 py-3 bg-gray-700 text-white rounded-lg text-xl hover:bg-gray-600"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    )
  }

  const summary = data?.summary || {}
  const settings = data?.settings || {}

  return (
    <div className="min-h-screen bg-gray-950 text-white p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <div className="text-3xl font-bold text-blue-400">5axis Health</div>
          <div className="text-xl text-gray-500">|</div>
          <div className="text-xl text-gray-400">{settings.dateRangeLabel || 'Last 30 Days'}</div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-mono text-white">
            {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div className="text-lg text-gray-500">
            {lastUpdated && `Updated ${lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <SummaryCard
          title="Revenue"
          value={formatCurrency(summary.total_revenue)}
          subtitle={`${formatNumber(summary.total_orders)} orders`}
          color="blue"
        />
        <SummaryCard
          title="Orders"
          value={formatNumber(summary.total_orders)}
          subtitle={`${formatNumber(summary.new_orders)} new, ${formatNumber(summary.refill_orders)} refills`}
          color="green"
        />
        <SummaryCard
          title="Margin"
          value={formatPercent(summary.margin_percent)}
          subtitle={formatCurrency(summary.gross_profit)}
          color="yellow"
        />
        <SummaryCard
          title="Expenses"
          value={formatCurrency(summary.total_expenses)}
          subtitle="COGS + Shipping"
          color="red"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Revenue by Brand Pie Chart */}
        <div className="bg-gray-900/50 rounded-2xl border border-gray-800 p-4">
          <div className="text-xl font-semibold text-gray-300 mb-4">Revenue by Brand</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => formatCurrency(value * 100)}
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '8px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Daily Revenue Trend */}
        <div className="bg-gray-900/50 rounded-2xl border border-gray-800 p-4">
          <div className="text-xl font-semibold text-gray-300 mb-4">Daily Revenue Trend</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={areaData}>
                <defs>
                  {data?.brand_breakdown?.map((brand, index) => (
                    <linearGradient key={brand.clinicId} id={`color-${brand.clinicId}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={brand.color} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={brand.color} stopOpacity={0.1} />
                    </linearGradient>
                  ))}
                </defs>
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  tick={{ fill: '#9ca3af', fontSize: 12 }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  stroke="#6b7280"
                  tick={{ fill: '#9ca3af', fontSize: 12 }}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  formatter={(value) => formatCurrency(value * 100)}
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '8px'
                  }}
                />
                {data?.brand_breakdown?.map((brand) => (
                  <Area
                    key={brand.clinicId}
                    type="monotone"
                    dataKey={brand.clinicId}
                    name={brand.displayName}
                    stackId="1"
                    stroke={brand.color}
                    fill={`url(#color-${brand.clinicId})`}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Brand Breakdown Table */}
      <div className="bg-gray-900/50 rounded-2xl border border-gray-800 p-4">
        <div className="text-xl font-semibold text-gray-300 mb-4">Brand Performance</div>
        <table className="w-full">
          <thead>
            <tr className="text-gray-400 text-lg border-b border-gray-700">
              <th className="text-left py-3 px-4">Brand</th>
              <th className="text-right py-3 px-4">Revenue</th>
              <th className="text-right py-3 px-4">Orders</th>
              <th className="text-right py-3 px-4">New</th>
              <th className="text-right py-3 px-4">Refills</th>
              <th className="text-right py-3 px-4">Margin</th>
            </tr>
          </thead>
          <tbody>
            {data?.brand_breakdown?.map((brand) => (
              <tr key={brand.clinicId} className="text-xl border-b border-gray-800">
                <td className="py-4 px-4 flex items-center gap-3">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: brand.color }}
                  ></div>
                  <span className="font-medium">{brand.displayName}</span>
                </td>
                <td className="text-right py-4 px-4 font-mono">
                  {formatCurrency(brand.summary.total_revenue)}
                </td>
                <td className="text-right py-4 px-4 font-mono">
                  {formatNumber(brand.summary.total_orders)}
                </td>
                <td className="text-right py-4 px-4 font-mono text-green-400">
                  {formatNumber(brand.summary.new_orders)}
                </td>
                <td className="text-right py-4 px-4 font-mono text-blue-400">
                  {formatNumber(brand.summary.refill_orders)}
                </td>
                <td className="text-right py-4 px-4 font-mono">
                  {formatPercent(brand.summary.margin_percent)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="text-xl font-bold bg-gray-800/50">
              <td className="py-4 px-4">Total</td>
              <td className="text-right py-4 px-4 font-mono">
                {formatCurrency(summary.total_revenue)}
              </td>
              <td className="text-right py-4 px-4 font-mono">
                {formatNumber(summary.total_orders)}
              </td>
              <td className="text-right py-4 px-4 font-mono text-green-400">
                {formatNumber(summary.new_orders)}
              </td>
              <td className="text-right py-4 px-4 font-mono text-blue-400">
                {formatNumber(summary.refill_orders)}
              </td>
              <td className="text-right py-4 px-4 font-mono">
                {formatPercent(summary.margin_percent)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Footer - small logout link for setup purposes */}
      <div className="fixed bottom-4 right-4 opacity-30 hover:opacity-100 transition-opacity">
        <button
          onClick={() => {
            logout()
            navigate('/login')
          }}
          className="text-sm text-gray-500 hover:text-gray-300"
        >
          Logout
        </button>
      </div>
    </div>
  )
}
